/**
 * Hybrid Storage Adapter for AWS Amplify Authentication
 *
 * This adapter enables multi-session per tab functionality while also supporting
 * session persistence when opening new tabs from the primary session.
 *
 * How it works:
 * - Normal login: Session stored in BOTH sessionStorage AND localStorage (as "primarySession")
 * - Token login (embeddables): Session stored ONLY in sessionStorage
 * - New tab load: Check sessionStorage first, fall back to localStorage primarySession
 *
 * This preserves the embeddable multi-session capability (different users per tab)
 * while allowing normal users to open new tabs without re-authenticating.
 */

const PRIMARY_SESSION_KEY = 'mj_primary_session';
const EMBEDDABLE_SESSION_FLAG = 'mj_embeddable_session';

const hybridStorage = {
  /**
   * Store a value in session storage, and optionally in localStorage as primary session backup.
   * Embeddable sessions (token-based login) skip the localStorage backup to maintain isolation.
   */
  setItem: (key, value) => {
    // Always store in sessionStorage for current tab
    window.sessionStorage.setItem(key, value);

    // Skip localStorage backup for embeddable sessions
    const isEmbeddable = window.sessionStorage.getItem(EMBEDDABLE_SESSION_FLAG);
    if (isEmbeddable) {
      return;
    }

    // Store Cognito auth keys in localStorage as primary session backup
    // This allows new tabs to inherit the session
    if (key.includes('CognitoIdentityServiceProvider')) {
      try {
        const primarySession = JSON.parse(
          localStorage.getItem(PRIMARY_SESSION_KEY) || '{}'
        );
        primarySession[key] = value;
        localStorage.setItem(PRIMARY_SESSION_KEY, JSON.stringify(primarySession));
      } catch (error) {
        console.warn('hybridStorage: Failed to backup session to localStorage', error);
      }
    }
  },

  /**
   * Retrieve a value from session storage, falling back to localStorage primary session.
   * When falling back, the value is copied to sessionStorage for this tab.
   */
  getItem: (key) => {
    // First try sessionStorage (current tab's session)
    let value = window.sessionStorage.getItem(key);
    if (value !== null) {
      return value;
    }

    // Fall back to localStorage primary session for new tabs
    // Only for Cognito auth keys
    if (key.includes('CognitoIdentityServiceProvider')) {
      try {
        const primarySession = JSON.parse(
          localStorage.getItem(PRIMARY_SESSION_KEY) || '{}'
        );
        if (primarySession[key]) {
          // Copy to sessionStorage for this tab (skip empty/falsy values)
          window.sessionStorage.setItem(key, primarySession[key]);
          return primarySession[key];
        }
      } catch (error) {
        console.warn('hybridStorage: Failed to read from localStorage', error);
      }
    }

    return null;
  },

  /**
   * Remove a value from session storage.
   * Does NOT remove from localStorage to preserve the primary session for other tabs.
   */
  removeItem: (key) => {
    window.sessionStorage.removeItem(key);
    // Intentionally NOT removing from localStorage primary session
    // This allows other tabs to remain authenticated
  },

  /**
   * Clear all session storage for the current tab.
   * Does NOT clear localStorage primary session by default.
   */
  clear: () => {
    window.sessionStorage.clear();
    // Intentionally NOT clearing localStorage primary session
    // Use clearPrimarySession() for full logout
  },

  /**
   * Clear the primary session from localStorage.
   * Call this on explicit logout to prevent session inheritance.
   */
  clearPrimarySession: () => {
    localStorage.removeItem(PRIMARY_SESSION_KEY);
  },

  /**
   * Mark the current session as an embeddable session.
   * Embeddable sessions are stored only in sessionStorage and don't overwrite
   * the primary session in localStorage.
   */
  markAsEmbeddable: () => {
    window.sessionStorage.setItem(EMBEDDABLE_SESSION_FLAG, 'true');
  },

  /**
   * Check if the current session is marked as embeddable.
   */
  isEmbeddable: () => {
    return window.sessionStorage.getItem(EMBEDDABLE_SESSION_FLAG) === 'true';
  },
};

export default hybridStorage;
